'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { type JSX, useEffect, useState } from 'react';
import { LandingProductLink } from '@/components/support-mainnet/landing-product-link';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useBetaAccess } from '@/hooks/use-beta-access';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { Link } from '@/i18n/navigation';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';

// Reveal once the user has scrolled past this many px — keeps the CTA off the
// hero on mount, then slides it in on the first deliberate scroll.
const REVEAL_AT_Y = 24;

// Floating product CTA shown on the landing page in place of the
// liquid-glass mobile nav (mobile / tablet only, `lg:hidden` — desktop has the
// product CTA in the slim landing header). Mirrors the market-detail Trade CTA
// (`mobile-trade-shell.tsx`) position and size EXACTLY so it sits at the same
// height from the bottom. Absent on mount; slides up on the first scroll and
// then stays. NetworkLink resolves `/markets` to the active network route.
export function LandingWaitlistCta() {
  const $ = _c(10);
  const t = useTranslations("supportMainnet.hero");
  const devnetEnabled = useFeatureGate("devnet_enabled");
  const hasBetaAccess = useBetaAccess();
  const prefersReduced = useReducedMotion();
  const [revealed, setRevealed] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const onScroll = function onScroll() {
        if (window.scrollY > REVEAL_AT_Y) {
          setRevealed(true);
        }
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", onScroll);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== devnetEnabled || $[3] !== hasBetaAccess || $[4] !== prefersReduced || $[5] !== revealed || $[6] !== t) {
    t2 = revealed && devnetEnabled !== null ? <m.div className="fixed bottom-2 left-0 right-0 z-sticky px-4 lg:hidden" initial={prefersReduced ? false : {
      y: 96,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={MOTION_ENTRANCE}><AnimatedButton asChild={true} variant="primary" size="xl" className="w-full">{devnetEnabled ? <LandingProductLink surface="mobile">{t("launchCtaWaitlist")}</LandingProductLink> : hasBetaAccess ? <LandingProductLink surface="mobile">{t("ctaEnterApp")}</LandingProductLink> : <Link href="/early-access">{t("ctaWaitlist")}</Link>}</AnimatedButton></m.div> : null;
    $[2] = devnetEnabled;
    $[3] = hasBetaAccess;
    $[4] = prefersReduced;
    $[5] = revealed;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <LazyMotion features={domAnimation} strict={true}><AnimatePresence>{t2}</AnimatePresence></LazyMotion>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}